import React from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw, CheckCircle, XCircle, Clock, Package, Mail, AlertCircle } from 'lucide-react';

const ReturnsExchanges = () => {
  const returnSteps = [
    {
      step: 1,
      title: 'Contact Us',
      description: 'Email or call within 30 days of delivery',
      icon: Mail,
    },
    {
      step: 2,
      title: 'Return Approval',
      description: 'We review and approve your return request',
      icon: CheckCircle,
    },
    {
      step: 3,
      title: 'Pickup Arranged',
      description: 'We schedule furniture pickup from your home',
      icon: Package,
    },
    {
      step: 4,
      title: 'Refund Processed',
      description: 'Refund issued after inspection (3-5 days)',
      icon: RotateCcw,
    },
  ];

  const eligibleReturns = [
    'Item is unused and in original condition',
    'All original packaging and tags included',
    'No signs of wear, damage, or assembly',
    'Returned within 30 days of delivery',
    'Item is not on final sale or clearance',
  ];

  const nonEligibleReturns = [
    'Custom or made-to-order furniture',
    'Clearance or final sale items',
    'Damaged after delivery acceptance',
    'Items showing signs of use or assembly',
    'Missing original packaging or parts',
  ];

  return (
    <div className="min-h-screen bg-dark-50">
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm font-body">
            <Link to="/" className="text-primary-600 hover:text-primary-700">Home</Link>
            <span className="text-dark-400">/</span>
            <span className="text-dark-700 font-semibold">Returns & Exchanges</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-primary py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center  animate-fade-in">
            <RotateCcw className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h1 className="text-4xl text-dark-900 md:text-5xl lg:text-6xl font-display font-bold mb-6">
              Returns & Exchanges
            </h1>
            <p className="text-xl md:text-2xl text-dark-700 max-w-3xl mx-auto font-body">
              We want you to love your furniture. If you're not satisfied, we're here to help.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        
        {/* 30-Day Return Policy */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12 animate-slide-up">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-20 h-20 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-accent-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-dark-900 mb-4">
              30-Day Return Policy
            </h2>
            <p className="text-lg text-dark-600 font-body mb-6">
              Changed your mind? No problem. Return eligible items within 30 days of delivery for a full refund minus restocking fee.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-success/10 border border-success rounded-xl text-success font-body font-semibold">
              <CheckCircle className="w-5 h-5" />
              Hassle-Free Returns
            </div>
          </div>
        </div>

        {/* Return Process */}
        <div className="mb-12">
          <h2 className="text-3xl font-display font-bold text-dark-900 mb-8 text-center">
            How to Return Your Furniture
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {returnSteps.map((item, index) => {
              const Icon = item.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-card p-6 text-center hover:shadow-soft transition-smooth"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <div className="w-10 h-10 bg-gradient-primary text-white rounded-full flex items-center justify-center mx-auto mb-3 font-display font-bold text-lg">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-display font-bold text-dark-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-dark-600 font-body text-sm">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Eligible vs Non-Eligible */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          
          {/* Eligible for Return */}
          <div className="bg-white rounded-2xl shadow-soft p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
              <h3 className="text-2xl font-display font-bold text-dark-900">
                Eligible for Return
              </h3>
            </div>
            
            <ul className="space-y-4">
              {eligibleReturns.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span className="text-dark-700 font-body">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Not Eligible for Return */}
          <div className="bg-white rounded-2xl shadow-soft p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center">
                <XCircle className="w-6 h-6 text-error" />
              </div>
              <h3 className="text-2xl font-display font-bold text-dark-900">
                Not Eligible for Return
              </h3>
            </div>
            
            <ul className="space-y-4">
              {nonEligibleReturns.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                  <span className="text-dark-700 font-body">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Exchanges */}
        <div className="bg-gradient-accent text-white rounded-2xl shadow-colored p-8 md:p-12 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <RotateCcw className="w-10 h-10" />
              </div>
              <h2 className="text-3xl font-display font-bold mb-4">
                Want to Exchange Instead?
              </h2>
              <p className="text-xl text-accent-100 font-body">
                Prefer a different color or size? We make exchanges easy!
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <h3 className="font-display font-bold text-lg mb-2">Same Price</h3>
                <p className="text-accent-100 font-body text-sm">
                  Exchange for items of equal value with no additional fees
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <h3 className="font-display font-bold text-lg mb-2">Quick Process</h3>
                <p className="text-accent-100 font-body text-sm">
                  We'll pickup the old item and deliver the new one
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <h3 className="font-display font-bold text-lg mb-2">30-Day Window</h3>
                <p className="text-accent-100 font-body text-sm">
                  Same 30-day timeframe applies to all exchanges
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Fees & Refunds */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-display font-bold text-dark-900 mb-8 text-center">
            Fees & Refund Details
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="p-6 bg-dark-50 rounded-xl border-l-4 border-primary-500">
              <h3 className="text-xl font-display font-bold text-dark-900 mb-2">
                Restocking Fee
              </h3>
              <p className="text-dark-700 font-body mb-2">
                15% restocking fee applies to all returns to cover inspection and repackaging costs
              </p>
              <p className="text-sm text-dark-600 font-body">
                <strong>Example:</strong> $1,000 item = $150 restocking fee, $850 refund
              </p>
            </div>

            <div className="p-6 bg-dark-50 rounded-xl border-l-4 border-accent-500">
              <h3 className="text-xl font-display font-bold text-dark-900 mb-2">
                Return Shipping
              </h3>
              <p className="text-dark-700 font-body mb-2">
                Customer responsible for return shipping costs (calculated based on distance)
              </p>
              <p className="text-sm text-dark-600 font-body">
                <strong>Note:</strong> Defective items are returned at our expense
              </p>
            </div>

            <div className="p-6 bg-dark-50 rounded-xl border-l-4 border-primary-500">
              <h3 className="text-xl font-display font-bold text-dark-900 mb-2">
                Refund Timeline
              </h3>
              <p className="text-dark-700 font-body">
                Refunds processed within 3-5 business days after item inspection. Original payment method credited within 5-10 business days.
              </p>
            </div>
          </div>
        </div>

        {/* Damaged or Defective */}
        <div className="bg-warning/10 border-2 border-warning rounded-2xl p-8 mb-12">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start gap-4 mb-6">
              <AlertCircle className="w-8 h-8 text-warning flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-display font-bold text-dark-900 mb-2">
                  Received Damaged or Defective Item?
                </h2>
                <p className="text-dark-700 font-body mb-4">
                  Don't worry! We'll make it right at no cost to you.
                </p>
              </div>
            </div>
            
            <div className="space-y-3 ml-12">
              <p className="flex items-start gap-2 text-dark-700 font-body">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <span><strong>Report immediately:</strong> Contact us within 48 hours of delivery</span>
              </p>
              <p className="flex items-start gap-2 text-dark-700 font-body">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <span><strong>Provide photos:</strong> Send clear photos of the damage or defect</span>
              </p>
              <p className="flex items-start gap-2 text-dark-700 font-body">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <span><strong>We'll resolve it:</strong> Free replacement or full refund, no fees</span>
              </p>
            </div>

            <div className="mt-6 p-4 bg-white rounded-lg">
              <p className="text-dark-900 font-body text-center">
                <strong>📧 Email:</strong> <a href="mailto:returns@skyfurniture.ca" className="text-primary-600 hover:underline">returns@skyfurniture.ca</a> or <strong>📞 Call:</strong> (123) 456-7890
              </p>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-display font-bold text-dark-900 mb-6 text-center">
            Need Help with a Return?
          </h2>
          
          <div className="max-w-3xl mx-auto">
            <p className="text-lg text-dark-600 font-body text-center mb-8">
              Our customer service team is here to assist you with returns, exchanges, or any questions you may have.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-primary-50 border border-primary-200 rounded-xl text-center">
                <Mail className="w-10 h-10 text-primary-600 mx-auto mb-3" />
                <h3 className="font-display font-bold text-dark-900 mb-2">Email Us</h3>
                <a href="mailto:returns@skyfurniture.ca" className="text-primary-600 hover:underline font-body">
                  returns@skyfurniture.ca
                </a>
                <p className="text-sm text-dark-600 font-body mt-2">
                  Response within 24 hours
                </p>
              </div>

              <div className="p-6 bg-accent-50 border border-accent-200 rounded-xl text-center">
                <Package className="w-10 h-10 text-accent-600 mx-auto mb-3" />
                <h3 className="font-display font-bold text-dark-900 mb-2">Call Us</h3>
                <a href="tel:+1234567890" className="text-accent-600 hover:underline font-body text-lg">
                  (123) 456-7890
                </a>
                <p className="text-sm text-dark-600 font-body mt-2">
                  Mon-Fri: 9AM-6PM EST
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-primary border border-primary-300 text-primary-700 rounded-xl font-body font-semibold text-lg hover:shadow-colored transition-smooth"
          >
            <Mail className="w-6 h-6" />
            Start a Return or Exchange
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReturnsExchanges;